import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageHeader from "../../core/common/header/PageHeader";
import {
  FaHome,
  FaChevronLeft,
  FaPencilAlt,
  FaPlus,
  FaEdit,
  FaTrash,
  FaUsers,
  FaSpinner,
  FaSync,
  FaChartBar,
  FaListAlt,
  FaClock,
} from "react-icons/fa";
import AdminBatchWritingTaskSummary from "./AdminBatchWritingTaskSummary";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type WritingTask = {
  id: number;
  level: string;
  taskNumber: number;
  taskDescription: string;
};

type AssignedBatch = {
  id: string;
  taskId: number;
  taskNumber: number;
  taskLevel: string;
  batchId: number;
  batchName: string;
  assignedDate: string;
  deadline: string;
  status: string;
};

const AdminWritingTasksPage: React.FC = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<WritingTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [showBatchResultsModal, setShowBatchResultsModal] = useState(false);

  // Assigned Batches state
  const [showAssignedBatchesModal, setShowAssignedBatchesModal] =
    useState(false);
  const [assignedBatches, setAssignedBatches] = useState<AssignedBatch[]>([]);
  const [assignedBatchesLoading, setAssignedBatchesLoading] = useState(false);
  const [activeAssignedTab, setActiveAssignedTab] = useState("A1");

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/writing-tasks`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setTasks(data);
        setLastUpdated(new Date());
      } else {
        setError("Failed to fetch writing tasks");
      }
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedBatches = async () => {
    setAssignedBatchesLoading(true);
    try {
      // Fetch all tasks first
      const tasksRes = await fetch(`${API_BASE_URL}/api/admin/writing-tasks`, {
        credentials: "include",
      });
      if (!tasksRes.ok) throw new Error("Failed to fetch tasks");
      const tasks = await tasksRes.json();

      // Create a batch lookup map (will be populated as we fetch assignments)
      const batchMap = new Map();

      // Try to fetch batches - if it fails, we'll use batch IDs
      try {
        const batchesRes = await fetch(
          `${API_BASE_URL}/api/admin/writing-tasks/active-batches`,
          {
            credentials: "include",
          }
        );
        if (batchesRes.ok) {
          const batches = await batchesRes.json();
          batches.forEach((batch: any) => {
            batchMap.set(batch.id, batch.name);
          });
        }
      } catch (err) {
        console.log("Could not fetch batch names, will use IDs");
      }

      // For each task, fetch its assignments
      const allAssignments: AssignedBatch[] = [];

      for (const task of tasks) {
        try {
          const assignRes = await fetch(
            `${API_BASE_URL}/api/admin/writing-tasks/${task.id}/assigned-batches`,
            { credentials: "include" }
          );

          if (assignRes.ok) {
            const assignments = await assignRes.json();

            // Transform the assignments
            assignments.forEach((assignment: any) => {
              const deadline = new Date(assignment.deadline);
              const now = new Date();

              // Determine status
              let status = "active";
              if (deadline < now) {
                status = "completed";
              } else if (
                deadline > new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000)
              ) {
                status = "upcoming";
              }

              allAssignments.push({
                id: `${task.id}-${
                  assignment.batchId
                }-${Date.now()}-${Math.random()}`,
                taskId: task.id,
                taskNumber: task.taskNumber,
                taskLevel: task.level || "Unspecified",
                batchId: assignment.batchId,
                batchName:
                  assignment.batchName ||
                  batchMap.get(assignment.batchId) ||
                  `Batch ${assignment.batchId}`,
                assignedDate:
                  assignment.assignedDate ||
                  assignment.createdAt ||
                  new Date().toISOString(),
                deadline: assignment.deadline,
                status: status,
              });
            });
          }
        } catch (err) {
          console.error(`Error fetching assignments for task ${task.id}:`, err);
        }
      }

      console.log("📊 Loaded assignments:", allAssignments);
      setAssignedBatches(allAssignments);
    } catch (err) {
      console.error("Failed to load assigned batches:", err);
      setAssignedBatches([]);
    } finally {
      setAssignedBatchesLoading(false);
    }
  };

  const handleViewAssignedBatches = () => {
    setShowAssignedBatchesModal(true);
    fetchAssignedBatches();
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchTasks();
    setRefreshing(false);
  };

  const handleDelete = async (taskId: number) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this writing task? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/writing-tasks/${taskId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (response.ok) {
        setTasks(tasks.filter((task) => task.id !== taskId));
      } else {
        const data = await response.json();
        alert(data.error || "Failed to delete task");
      }
    } catch (err) {
      console.error("Error deleting task:", err);
      alert("Network error. Please try again.");
    }
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Group tasks by level
  const tasksByLevel = tasks.reduce<Record<string, WritingTask[]>>(
    (acc, task) => {
      const level = task.level || "Unspecified";
      if (!acc[level]) acc[level] = [];
      acc[level].push(task);
      return acc;
    },
    {}
  );

  // Sort levels in order: A1, A2, B1, B2, C1, C2
  const levelOrder = ["A1", "A2", "B1", "B2", "C1", "C2", "Unspecified"];
  const sortedLevels = Object.keys(tasksByLevel).sort((a, b) => {
    const indexA = levelOrder.indexOf(a);
    const indexB = levelOrder.indexOf(b);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });

  // Group assigned batches by level
  const assignedBatchesByLevel = assignedBatches.reduce<
    Record<string, AssignedBatch[]>
  >((acc, assignment) => {
    const level = assignment.taskLevel || "Unspecified";
    if (!acc[level]) acc[level] = [];
    acc[level].push(assignment);
    return acc;
  }, {});

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<
      string,
      { label: string; icon: JSX.Element; className: string }
    > = {
      active: {
        label: "Active",
        icon: <FaClock />,
        className: "bg-green-100 text-green-800",
      },
      upcoming: {
        label: "Upcoming",
        icon: <FaClock />,
        className: "bg-yellow-100 text-yellow-800",
      },
      completed: {
        label: "Completed",
        icon: <FaClock />,
        className: "bg-blue-100 text-blue-800",
      },
      expired: {
        label: "Expired",
        icon: <FaClock />,
        className: "bg-red-100 text-red-800",
      },
    };

    const config = statusConfig[status.toLowerCase()] || statusConfig.active;

    return (
      <span
        className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-semibold ${config.className}`}
      >
        {config.icon}
        {config.label}
      </span>
    );
  };

  const renderAssignedBatchesTable = (level: string) => {
    const assignments = assignedBatchesByLevel[level] || [];

    if (assignments.length === 0) {
      return (
        <div className="text-center text-gray-400 py-10 bg-white rounded-lg shadow">
          <FaPencilAlt className="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p className="font-semibold">No assignments for this level yet.</p>
        </div>
      );
    }

    // Get unique batches with their latest assignment date
    const batchAssignmentDates = new Map();
    assignments.forEach((a) => {
      const currentDate = batchAssignmentDates.get(a.batchId);
      if (!currentDate || new Date(a.assignedDate) > new Date(currentDate)) {
        batchAssignmentDates.set(a.batchId, a.assignedDate);
      }
    });

    const uniqueBatches = Array.from(
      new Map(
        assignments.map((a) => [
          a.batchId,
          { id: a.batchId, name: a.batchName },
        ])
      ).values()
    ).sort((a, b) => {
      // Sort by latest assignment date descending (newest first)
      const dateA = new Date(batchAssignmentDates.get(a.id) || 0);
      const dateB = new Date(batchAssignmentDates.get(b.id) || 0);
      return dateB.getTime() - dateA.getTime();
    });

    // Always show Task 1 to Task 14
    const taskColumns = Array.from({ length: 14 }, (_, i) => i + 1);

    return (
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead className="sticky top-0 z-30">
              <tr className="bg-gradient-to-r from-[#009fe3] to-cyan-500">
                <th className="sticky left-0 z-40 bg-[#009fe3] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-white border-r-2 border-white">
                  Batch Name
                </th>
                {taskColumns.map((taskNum) => (
                  <th
                    key={taskNum}
                    className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-white border-r border-cyan-400 min-w-[100px]"
                  >
                    <span className="font-bold text-base">Task {taskNum}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {uniqueBatches.map((batch, batchIdx) => (
                <tr
                  key={batch.id}
                  className={`${
                    batchIdx % 2 === 0 ? "bg-white" : "bg-gray-50"
                  } hover:bg-blue-50 transition-colors`}
                >
                  <td className="sticky left-0 z-10 bg-inherit px-4 py-3 font-semibold text-gray-900 border-r-2 border-gray-200">
                    {batch.name}
                  </td>
                  {taskColumns.map((taskNum) => {
                    // Get the Nth task for this batch (sorted by taskNumber)
                    const batchAssignments = assignments
                      .filter((a) => a.batchId === batch.id)
                      .sort((x, y) => x.taskNumber - y.taskNumber);

                    const actualAssignment = batchAssignments[taskNum - 1];

                    return (
                      <td
                        key={`${batch.id}-task${taskNum}`}
                        className="px-3 py-3 text-center border-r border-gray-200"
                      >
                        {actualAssignment ? (
                          <div className="flex flex-col items-center gap-1">
                            <div className="text-sm font-semibold text-gray-900">
                              {new Date(
                                actualAssignment.deadline
                              ).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                              })}
                            </div>
                            <div className="text-xs text-gray-500">
                              {new Date(
                                actualAssignment.deadline
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                              })}
                            </div>
                            <div className="mt-1">
                              {getStatusBadge(actualAssignment.status)}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-300 text-xl">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderTaskTable = (level: string, taskList: WritingTask[]) => {
    // Sort tasks by task number
    const sortedTasks = [...taskList].sort(
      (a, b) => a.taskNumber - b.taskNumber
    );

    return (
      <div className="mb-10" key={level}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-bold text-[#009fe3] flex items-center gap-2">
            <FaPencilAlt />
            Level: {level}
          </h3>
          <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-md">
            {sortedTasks.length} / 14 tasks
          </span>
        </div>
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                    Task #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                    Level
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                    Task Description
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {sortedTasks.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="py-10 text-center text-gray-400 font-semibold"
                    >
                      No writing tasks found for this level.
                    </td>
                  </tr>
                ) : (
                  sortedTasks.map((task, index) => (
                    <tr
                      key={task.id}
                      className={`${
                        index % 2 === 1 ? "bg-gray-50" : "bg-white"
                      } hover:bg-blue-50 transition-colors`}
                    >
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-800 font-bold rounded-md">
                          {task.taskNumber}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-800">
                          {task.level}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-700 text-sm">
                          {truncateText(task.taskDescription, 120)}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() =>
                              navigate(`/admin/writing-tasks/${task.id}/assign`)
                            }
                            className="inline-flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-semibold"
                            title="Assign to Batches"
                          >
                            <FaUsers size={14} />
                            Assign
                          </button>
                          <button
                            onClick={() =>
                              navigate(`/admin/writing-tasks/${task.id}/edit`)
                            }
                            className="inline-flex items-center gap-1 px-3 py-2 bg-[#009fe3] text-white rounded-md hover:bg-cyan-600 transition-colors text-sm font-semibold"
                            title="Edit Task"
                          >
                            <FaEdit size={14} />
                            Edit
                          </button>

                          <button
                            onClick={() => handleDelete(task.id)}
                            className="inline-flex items-center gap-1 px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-semibold"
                            title="Delete Task"
                          >
                            <FaTrash size={14} />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaPencilAlt />}
        title="Manage Writing Tasks"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Writing Tasks" },
        ]}
      />

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8 bg-white rounded-lg shadow-lg p-4">
          <button
            onClick={() => navigate("/admin/writing-tasks/create")}
            className="flex items-center justify-center gap-2 px-5 py-3 rounded-md font-semibold bg-[#009fe3] hover:bg-cyan-600 text-white shadow-md transition flex-1 sm:flex-initial"
          >
            <FaPlus />
            Create Writing Task
          </button>
          <button
            onClick={handleViewAssignedBatches}
            className="flex items-center justify-center gap-2 px-5 py-3 rounded-md font-semibold bg-purple-600 hover:bg-purple-700 text-white shadow-md transition flex-1 sm:flex-initial"
          >
            <FaListAlt />
            View Assigned Batches
          </button>
          <button
            onClick={() => setShowBatchResultsModal(true)}
            className="flex items-center justify-center gap-2 px-5 py-3 rounded-md font-semibold bg-green-600 hover:bg-green-700 text-white shadow-md transition flex-1 sm:flex-initial"
          >
            <FaChartBar />
            Batch Results
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-md">
            <p className="font-semibold">Error</p>
            <p>{error}</p>
          </div>
        )}

        {loading ? (
          <div className="text-center text-[#009fe3] font-semibold py-20 text-xl flex items-center justify-center gap-2">
            <FaSpinner className="animate-spin" />
            Loading writing tasks...
          </div>
        ) : (
          <>
            {sortedLevels.length === 0 ? (
              <div className="text-center text-gray-400 py-10 bg-white rounded-lg shadow-lg">
                <FaPencilAlt className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold mb-2">
                  No Writing Tasks Yet
                </h3>
                <p className="mb-4">
                  Create your first writing task to get started!
                </p>
                <button
                  onClick={() => navigate("/admin/writing-tasks/create")}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#009fe3] text-white rounded-md hover:bg-cyan-600 transition-colors font-semibold"
                >
                  <FaPlus />
                  Create Writing Task
                </button>
              </div>
            ) : (
              sortedLevels.map((level) =>
                renderTaskTable(level, tasksByLevel[level])
              )
            )}
          </>
        )}
      </main>

      {/* Assigned Batches Modal */}
      {showAssignedBatchesModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
          onClick={() => setShowAssignedBatchesModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-[95vw] h-[90vh] flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowAssignedBatchesModal(false)}
              className="absolute top-4 right-4 z-10 text-gray-600 hover:text-gray-900 text-3xl font-bold bg-white rounded-full w-10 h-10 flex items-center justify-center shadow-md hover:shadow-lg transition-all"
              aria-label="Close"
            >
              &times;
            </button>

            {/* Header - Fixed */}
            <div className="flex-shrink-0 p-6 border-b border-gray-200 bg-white z-50">
              <h3 className="text-2xl font-bold text-[#009fe3] flex items-center gap-2">
                <FaListAlt />
                Assigned Batches & Deadlines
              </h3>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              {assignedBatchesLoading ? (
                <div className="text-center text-[#009fe3] flex items-center justify-center gap-2 py-10">
                  <FaSpinner className="animate-spin" />
                  Loading assigned batches...
                </div>
              ) : (
                <>
                  {/* Level Tabs - Sticky */}
                  <div className="sticky top-0 bg-white z-40 pb-4 mb-6 border-b border-gray-200">
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {levelOrder.slice(0, 6).map((level) => {
                        const count =
                          assignedBatchesByLevel[level]?.length || 0;
                        return (
                          <button
                            key={level}
                            onClick={() => setActiveAssignedTab(level)}
                            className={`px-4 py-2 rounded-t-lg font-semibold text-sm whitespace-nowrap transition-colors ${
                              activeAssignedTab === level
                                ? "bg-[#009fe3] text-white"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                          >
                            Level {level}
                            {count > 0 && (
                              <span
                                className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                                  activeAssignedTab === level
                                    ? "bg-white/20"
                                    : "bg-gray-300"
                                }`}
                              >
                                {count}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Table for Active Tab */}
                  {renderAssignedBatchesTable(activeAssignedTab)}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Batch Results Modal */}
      {showBatchResultsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
          onClick={() => setShowBatchResultsModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-auto p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowBatchResultsModal(false)}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 text-3xl font-bold"
              aria-label="Close"
            >
              &times;
            </button>
            <h3 className="text-2xl font-bold mb-6 text-[#009fe3] flex items-center gap-2">
              <FaChartBar />
              Batch Writing Task Results Summary
            </h3>
            <AdminBatchWritingTaskSummary />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWritingTasksPage;
